import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import {
    signInWithEmailAndPassword,
    signInWithPopup,
    GoogleAuthProvider,
    signOut,
    onAuthStateChanged,
    User as FirebaseUser
} from 'firebase/auth';
import { auth } from '../firebase/config';
import { Employee } from '../types/hr';
import { offlineAuthService } from '../services/offlineAuthService';
import { clearDataCaches, clearLegacyDataCaches } from '../utils/dataCache';
import { notificationService } from '../services/notificationService';
import { employeeService } from '../services/employeeService';
import { isActivePanelEmployee, panelAccessFromEmployee } from '../auth/adminAccess';
// import { analyticsService } from '../services/analyticsService';

interface AuthState {
    isAuthenticated: boolean;
    user: Employee | null;
    loading: boolean;
    error: string | null;
}

interface AuthContextProps {
    currentUser: FirebaseUser | null;
    userData: Employee | null;
    login: (email: string, password: string) => Promise<void>;
    loginWithGoogle: () => Promise<void>;
    logout: () => Promise<void>;
    refreshUserData: () => Promise<void>;
    authState: AuthState;
    isOfflineMode: boolean;
}

const AuthContext = createContext<AuthContextProps | undefined>(undefined);

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

interface AuthProviderProps {
    children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
    const [currentUser, setCurrentUser] = useState<FirebaseUser | null>(null);
    const [userData, setUserData] = useState<Employee | null>(null);
    const [isOfflineMode, setIsOfflineMode] = useState<boolean>(false);
    const [authState, setAuthState] = useState<AuthState>({
        isAuthenticated: false,
        user: null,
        loading: true,
        error: null
    });

    useEffect(() => {
        clearLegacyDataCaches();
    }, []);

    // Check for FCM token refresh after service worker updates
    useEffect(() => {
        const refreshTokenFlag = localStorage.getItem('refreshFCMTokenAfterSW');
        if (refreshTokenFlag === 'true' && currentUser?.uid) {
        // console.log('[AUTH_CONTEXT] Refreshing FCM token after service worker update');
        
        // Clear the flag
        localStorage.removeItem('refreshFCMTokenAfterSW');
        
        // Refresh FCM token
        notificationService.addFCMTokenToUser(currentUser.uid, true)
            .then(() => {
            // console.log('[AUTH_CONTEXT] FCM token refreshed successfully after SW update');
            })
            .catch(error => {
            // console.error('[AUTH_CONTEXT] Failed to refresh FCM token after SW update:', error);
            });
        }
    }, [currentUser]);

    // Monitor online/offline status
    useEffect(() => {
        // Guard against SSR - only run in browser environment
        if (typeof window === 'undefined') {
        return;
        }

        const handleOnline = () => {
        console.log('[AUTH_CONTEXT] Network came back online');
        setIsOfflineMode(false);
        // When coming back online, re-validate auth state with Firebase
        // The onAuthStateChanged listener will handle this automatically
        };
    
        const handleOffline = () => {
        setIsOfflineMode(true);
        // Do not override Firebase auth state here. The Firebase SDK persists auth
        // in IndexedDB and remains valid while offline. Overwriting authState from
        // the cache on every offline event (including brief network blips) causes
        // PrivateRoute to re-check Firestore while offline, which then fails and
        // kicks the user out. The cache is only used for the initial load below.
        };

        // Set initial offline state - check if navigator exists
        setIsOfflineMode(typeof navigator !== 'undefined' ? !navigator.onLine : false);

        window.addEventListener('online', handleOnline);
        window.addEventListener('offline', handleOffline);

        return () => {
        window.removeEventListener('online', handleOnline);
        window.removeEventListener('offline', handleOffline);
        };
    }, []);

    // Initialize with cached auth state if offline
    useEffect(() => {
        // Guard against SSR - only run in browser environment
        if (typeof window === 'undefined' || typeof navigator === 'undefined') {
        return;
        }

        if (!navigator.onLine) {
        const cachedAuth = offlineAuthService.getCachedAuthState();
        if (cachedAuth) {
            // console.log('[AUTH_CONTEXT] Initializing with cached auth state (offline)');
            setUserData(cachedAuth.userData);
            setAuthState({
            isAuthenticated: cachedAuth.isAuthenticated,
            user: cachedAuth.userData,
            loading: false,
            error: null
            });
            setIsOfflineMode(true);
        } else {
            // No cached auth and offline - user needs to be online to authenticate
            setAuthState({
            isAuthenticated: false,
            user: null,
            loading: false,
            error: null
            });
        }
        }
    }, []);

    // Login function
    const login = async (email: string, password: string) => {
        if (typeof navigator !== 'undefined' && !navigator.onLine) {
        throw new Error('Login requires an internet connection');
        }

        try {
        // console.log(`[AUTH_CONTEXT] ${new Date().toISOString()} - Login function called`);
        setAuthState({ ...authState, loading: true, error: null });
        // console.log(`[AUTH_CONTEXT] ${new Date().toISOString()} - About to call signInWithEmailAndPassword`);
        await signInWithEmailAndPassword(auth, email, password);
        // console.log(`[AUTH_CONTEXT] ${new Date().toISOString()} - signInWithEmailAndPassword completed, waiting for onAuthStateChanged...`);
        
        // Track login event in analytics
        // analyticsService.trackAuth('login', 'email');
        } catch (error) {
        // console.log(`[AUTH_CONTEXT] ${new Date().toISOString()} - Login error:`, error);
        setAuthState({
            ...authState,
            loading: false,
            error: (error as Error).message
        });
        throw error;
        }
    };

    // Google login function
    const loginWithGoogle = async () => {
        if (typeof navigator !== 'undefined' && !navigator.onLine) {
        throw new Error('Login requires an internet connection');
        }

        try {
        setAuthState({ ...authState, loading: true, error: null });
        const provider = new GoogleAuthProvider();
        await signInWithPopup(auth, provider);
        } catch (error) {
        setAuthState({
            ...authState,
            loading: false,
            error: (error as Error).message
        });
        throw error;
        }
    };

    // Refresh user data function
    const refreshUserData = async () => {
        if (!currentUser) return;
        
        try {
        // console.log(`[AUTH_CONTEXT] ${new Date().toISOString()} - Refreshing user data for user: ${currentUser.uid}`);
        const userData = await employeeService.getEmployee(currentUser.uid);
        
        if (userData) {
            // console.log(`[AUTH_CONTEXT] ${new Date().toISOString()} - User data refreshed successfully`);
            
            // Update cached auth state for offline use
            offlineAuthService.cacheAuthState(currentUser.uid, userData, currentUser.email || '');
            
            setUserData(userData);
            setAuthState(prev => ({
            ...prev,
            user: userData,
            }));
        }
        } catch (error) {
        console.error(`[AUTH_CONTEXT] ${new Date().toISOString()} - Error refreshing user data:`, error);
        }
    };

    // Logout function
    const logout = async () => {
        try {
        setAuthState({ ...authState, loading: true, error: null });
        
        // Track logout event in analytics
        // analyticsService.trackAuth('logout');
        // analyticsService.setUser(null);
        
        // signOut clears the persisted session locally, so it works offline.
        await signOut(auth);
        offlineAuthService.clearAuthState();
        clearDataCaches();
        
        // Clear local state
        setCurrentUser(null);
        setUserData(null);
        setAuthState({
            isAuthenticated: false,
            user: null,
            loading: false,
            error: null
        });
        } catch (error) {
        setAuthState({
            ...authState,
            loading: false,
            error: (error as Error).message
        });
        throw error;
        }
    };

    // Listen for auth state changes and fetch user data
    useEffect(() => {
        // Don't set up Firebase listener if offline - use cached state instead
        if (typeof navigator !== 'undefined' && !navigator.onLine) {
        return;
        }

        const unsubscribe = onAuthStateChanged(auth, async (user) => {
        // console.log(`[AUTH_CONTEXT] ${new Date().toISOString()} - onAuthStateChanged triggered, user:`, user ? user.email : 'null');
        setCurrentUser(user);
        setAuthState(prev => ({ ...prev, loading: true }));
        
        if (user) {
            try {
            // console.log(`[AUTH_CONTEXT] ${new Date().toISOString()} - Fetching user data from Firestore for user: ${user.uid}`);
            const userData = await employeeService.getEmployee(user.uid);
            
            if (userData && isActivePanelEmployee(panelAccessFromEmployee(userData))) {
                // console.log(`[AUTH_CONTEXT] ${new Date().toISOString()} - User data found, setting authenticated state`);
                
                // Cache auth state for offline use
                offlineAuthService.cacheAuthState(user.uid, userData, user.email || '');
                
                // Set user ID for analytics
                // analyticsService.setUser(user.uid);
                // analyticsService.setUserProperties({
                // sport: userData.sport,
                // role: userData.role,
                // onboarding_complete: userData.onboardingComplete,
                // });
                
                setUserData(userData);
                setAuthState({
                isAuthenticated: true,
                user: userData,
                loading: false,
                error: null
                });
            } else if (userData) {
                offlineAuthService.clearAuthState();
                setUserData(null);
                setAuthState({
                isAuthenticated: false,
                user: null,
                loading: false,
                error: 'This account is not an active admin'
                });
            } else {
                console.log(`[AUTH_CONTEXT] ${new Date().toISOString()} - User data not found in Firestore`);
                setAuthState({
                isAuthenticated: true,
                user: null,
                loading: false,
                error: 'User data not found'
                });
            }
            } catch (error) {
            console.log(`[AUTH_CONTEXT] ${new Date().toISOString()} - Error fetching user data:`, error);
            
            // If network error and we have cached auth, use it
            if (typeof navigator !== 'undefined' && !navigator.onLine) {
                const cachedAuth = offlineAuthService.getCachedAuthState();
                if (cachedAuth) {
                setUserData(cachedAuth.userData);
                setAuthState({
                    isAuthenticated: cachedAuth.isAuthenticated,
                    user: cachedAuth.userData,
                    loading: false,
                    error: null
                });
                return;
                }
            }
            
            setAuthState({
                isAuthenticated: false,
                user: null,
                loading: false,
                error: (error as Error).message
            });
            }
        } else {
            // console.log(`[AUTH_CONTEXT] ${new Date().toISOString()} - No user, setting unauthenticated state`);
            setUserData(null);
            clearDataCaches();
            
            setAuthState({
            isAuthenticated: false,
            user: null,
            loading: false,
            error: null
            });
        }
        });

        return unsubscribe;
    }, []);

    const value = {
        currentUser,
        userData,
        login,
        loginWithGoogle,
        logout,
        refreshUserData,
        authState,
        isOfflineMode
    };

    return (
        <AuthContext.Provider value={value}>
        {children}
        </AuthContext.Provider>
    );
};

export default AuthContext; 